import { fromEvent } from 'rxjs';
import CountdownTimer from './countdownTimer';

/**
 * VerificationCodeInput class handles the input of a verification code.
 */
export default class VerificationCodeInput {
  // this is just for eslint to not complain
  htmlClass = 'nhs-c-input__wrapper';

  inputEventSubscription$ = null;

  pasteEventSubscription$ = null;

  keydownEventSubscription$ = null;

  codeInsertedCallback = null;

  /**
   * Constructor for initializing the VerificationCodeInput object with specified callbacks.
   *
   * @param {Function} validCodeCallback - The callback function to be executed when a valid code is inserted.
   * @param {Function} sendCodeCallback - The callback function to be executed to send a code.
   * @param {Function} onCountdownEnd - The callback function to be executed when the countdown ends.
   * @param {Function} onIncompleteCodeCallback - The callback function to be executed when a code is incomplete.
   * @return {VerificationCodeInput} A new instance of the VerificationCodeInput with the provided callbacks.
   */
  constructor(validCodeCallback, sendCodeCallback, onCountdownEnd, onIncompleteCodeCallback) {
    this.codeInsertedCallback = validCodeCallback;
    this.sendCodeCallback = sendCodeCallback;
    this.onCountdownEnd = onCountdownEnd;
    this.onIncompleteCodeCallback = onIncompleteCodeCallback;
  }

  /**
   * Returns the container element for the code fields.
   * @returns {Element}
   */
  static getCodeFieldsContainer() {
    return document.querySelector('[data-self-guided-tour-code-input-container]');
  }

  /**
   * Returns the container element for the verification code input.
   * @returns {Element}
   */
  static getContainer() {
    return document.querySelector('[data-self-guided-tour-codes-wrapper]');
  }

  /**
   * Returns the countdown element.
   * @returns {Element}
   */
  static getCountdown() {
    return document.querySelector('[data-self-guided-tour-countdown]');
  }

  /**
   * Returns the resend code button element.
   * @returns {Element}
   */
  static getResendCodeButton() {
    return document.querySelector('[data-self-guided-tour-resend-code]');
  }

  /**
   * Returns the verification code entered by the user.
   * @returns {string}
   */
  static getCode() {
    const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
      '[data-self-guided-tour-code-input]'
    );
    return Array.from(inputs)
      .map((input) => input.value.trim())
      .join('');
  }

  /**
   * Checks if all input fields are filled.
   * @returns {this is Element[]}
   */
  static checkAllInputsFilled() {
    const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
      '[data-self-guided-tour-code-input]'
    );
    return Array.from(inputs).every((input) => input.value.trim() !== '');
  }

  /**
   * Displays the error message for incorrect code.
   */
  static showError() {
    const wrapper = VerificationCodeInput.getContainer();
    wrapper.classList.add('nhs-c-input__wrapper--error');
  }

  /**
   * Hides the error message for incorrect code.
   */
  static hideError() {
    const wrapper = VerificationCodeInput.getContainer();
    wrapper.classList.remove('nhs-c-input__wrapper--error');
  }

  /**
   * Disables all input fields in the verification code input container.
   */
  static disableInputs() {
    const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
      '[data-self-guided-tour-code-input]'
    );
    inputs.forEach((input) => {
      input.disabled = true;
    });
  }

  /**
   * Enables all input fields in the verification code input container.
   */
  static enableInputs() {
    const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
      '[data-self-guided-tour-code-input]'
    );
    inputs.forEach((input) => {
      input.disabled = false;
    });
  }

  /**
   * Sets the disabled state of the resend code button.
   * @param {Boolean} flag
   */
  static setResendCodeButtonDisabled(flag) {
    const resendCodeButton = VerificationCodeInput.getResendCodeButton();
    resendCodeButton.disabled = flag;
  }

  static clearInputs() {
    const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
      '[data-self-guided-tour-code-input]'
    );
    inputs.forEach((input) => {
      input.value = '';
    });
  }

  /**
   * Sets focus to the first input field in the verification code input container.
   */
  static setFocusToCodeInput() {
    VerificationCodeInput.getCodeFieldsContainer().querySelector('[data-self-guided-tour-code-input]').focus();
  }

  /**
   * Handles the expiration of the verification code input.
   */
  handleExpireTime = () => {
    VerificationCodeInput.disableInputs();
    VerificationCodeInput.getCountdown().innerHTML = 'Code expired';
    this.onCountdownEnd();
  };

  /**
   * Binds events to the input fields and buttons in the verification code input container.
   */
  bindEvents() {
    const codeFieldsContainer = VerificationCodeInput.getCodeFieldsContainer();
    this.inputEventSubscription$ = fromEvent(codeFieldsContainer, 'input').subscribe(this.handleInput);
    this.pasteEventSubscription$ = fromEvent(codeFieldsContainer, 'keydown').subscribe(this.handleKeydown);
    this.keydownEventSubscription$ = fromEvent(codeFieldsContainer, 'paste').subscribe(this.handlePaste);
    this.countdownTimer = new CountdownTimer('[data-self-guided-tour-countdown]', this.handleExpireTime, {
      label: 'Code expires in: ',
    });
    this.resendCodeButton$ = fromEvent(VerificationCodeInput.getResendCodeButton(), 'click').subscribe(
      this.onResendCodeClick
    );
  }

  /**
   * Handles the click event of the resend code button.
   */
  onResendCodeClick = () => {
    VerificationCodeInput.clearInputs();
    VerificationCodeInput.enableInputs();
    VerificationCodeInput.setFocusToCodeInput();
    this.sendCodeCallback();
  };

  /**
   * Handles the paste event in the input fields.
   * @param event
   */
  handlePaste = (event) => {
    const target = event.target;
    if (target.dataset.selfGuidedTourCodeInput !== undefined) {
      const pasteData = event.clipboardData.getData('text');
      const inputs = VerificationCodeInput.getCodeFieldsContainer().querySelectorAll(
        '[data-self-guided-tour-code-input]'
      );
      if (pasteData.length === inputs.length) {
        pasteData.split('').forEach((char, i) => {
          if (inputs[i]) {
            inputs[i].value = char;
          }
        });
        inputs[inputs.length - 1].focus();
      }
      const code = VerificationCodeInput.getCode();
      if (typeof this.codeInsertedCallback === 'function') {
        this.codeInsertedCallback(code);
      }

      event.preventDefault();
    }
  };

/**
  * Handles the input event in the input fields.
  * @param event
  */
  handleInput = (event) => {
    const target = event.target;
    if (target.dataset.selfGuidedTourCodeInput !== undefined) {
      const value = target.value.trim();
      if (value.length === 1) {
        const nextInput = target.nextElementSibling;
        if (nextInput && nextInput.dataset.selfGuidedTourCodeInput !== undefined) {
          nextInput.focus();
        }
      }
      else if (value && value.length > 1) {
        // This is a workaround for mobile chrome which does not fire onPaste event from sms auto-populate.
        this.handlePaste({
          target,
          clipboardData: {
            getData: () => value
          },
          preventDefault: () => { } // Mock preventDefault to avoid errors
        });
      }
      if (VerificationCodeInput.checkAllInputsFilled()) {
        const code = VerificationCodeInput.getCode();
        if (typeof this.codeInsertedCallback === 'function') {
          this.codeInsertedCallback(code);
        }
      } else if (typeof this.onIncompleteCodeCallback === 'function') {
        this.onIncompleteCodeCallback();
      }
    }
  };

/**
  * Handles the keydown event in the input fields.
  * @param event
  */
  handleKeydown = (event) => {
    const keyPressed = event.key;
    const target = event.target;
    if (keyPressed === 'Backspace' && target.hasAttribute('data-self-guided-tour-code-input') &&
      !target.value) { // if backspace is pressed on an empty input
      const prevInput = target.previousElementSibling;
      if (prevInput && prevInput.hasAttribute('data-self-guided-tour-code-input')) { // and there is a previous input
        prevInput.focus();
        if (typeof this.codeInsertedCallback === 'function') {
          this.codeInsertedCallback(null);
        }
      }
    }
    // Allow Ctrl+V (paste)
    if ((event.ctrlKey || event.metaKey) && keyPressed.toLowerCase() === 'v') {
      // Allow paste
      return;
    }
    // Only allow single digit number keys (0-9), block everything else except special keys (e.g., Tab, Arrow, etc.)
    if (keyPressed.length === 1 && !keyPressed.match(/^[0-9]$/)) {
      event.preventDefault();
    }
  };

  /**
   * Unbinds all events from the input fields and buttons in the verification code input container.
   */
  unbindEvents() {
    if (this.inputEventSubscription$) {
      this.inputEventSubscription$.unsubscribe();
      this.inputEventSubscription$ = null;
    }
    if (this.pasteEventSubscription$) {
      this.pasteEventSubscription$.unsubscribe();
      this.pasteEventSubscription$ = null;
    }
    if (this.keydownEventSubscription$) {
      this.keydownEventSubscription$.unsubscribe();
      this.keydownEventSubscription$ = null;
    }
    if (this.resendCodeButton$) {
      this.resendCodeButton$.unsubscribe();
      this.resendCodeButton$ = null;
    }
  }

  /**
   * Initializes the verification code input by binding events and starting the countdown.
   */
  startCountdown() {
    this.countdownTimer.start();
  }

  /**
   * Stops the countdown timer.
   */
  stopCountdown() {
    this.countdownTimer.stop();
  }

  /**
   * Renders the verification code input component.
   * @returns {string}
   */
  render() {
    return `<div class="${this.htmlClass}" data-self-guided-tour-codes-wrapper hidden>
      <label class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">
        Code
      </label>
      <div data-self-guided-tour-code-input-container class="nhs-c-input__code-fields nhs-mq-input__code-fields--large">
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
        <input data-self-guided-tour-code-input type="tel" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{1}" class="nhs-c-input__field nhs-c-input__field--code" required />
      </div>
      <span data-code-error-label class="nhs-c-input__label--error">This code is incorrect</span>

      <div class="nhs-o-flex-row-justify-content-space-between-self-stretch nhs-u-margin--top-600">
        <p class="nhs-c-label--black-1000-body-small-regular" data-self-guided-tour-countdown></p>
        <button type="button" data-self-guided-tour-resend-code
                class="nhs-mq-button-resend-code--position-extra-large nhs-c-label--black-1000-body-small-regular nhs-u-text-underline nhs-u-cursor--pointer">
          Resend code
        </button>
      </div>
    </div>`;
  }
}